import React, { useRef, useState } from "react";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import Swal from "sweetalert2";

// Reusable Excel bulk-upload card for the Inventory pages.
//
// columns:    [{ key, header, aliases?: string[], required?: boolean }]
// sampleRows: array of objects keyed by column key (written into the template)
// onSubmit:   async (rows) => response data { created, failed, errors }
//             rows are [{ row, ...keyed values }] — `row` is the Excel row no.
//
// The file is read in the browser; only plain rows are sent to the server.

const norm = (s) =>
  String(s ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const cellText = (v) => {
  if (v === null || v === undefined) return "";
  if (typeof v === "object") {
    if (v instanceof Date) return v.toISOString().slice(0, 10);
    if (Array.isArray(v.richText)) return v.richText.map((t) => t.text).join("");
    if (v.result !== undefined) return cellText(v.result);
    if (v.text !== undefined) return cellText(v.text);
    return "";
  }
  return String(v).trim();
};

const BulkUploadCard = ({
  title,
  description,
  columns,
  sampleRows = [],
  templateName,
  onSubmit,
  onDone,
}) => {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const downloadTemplate = async () => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Upload");
    ws.columns = columns.map((c) => ({
      header: c.required ? `${c.header} *` : c.header,
      key: c.key,
      width: Math.max(18, c.header.length + 6),
    }));
    ws.getRow(1).font = { bold: true };
    sampleRows.forEach((r) => ws.addRow(r));
    const buffer = await wb.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      templateName,
    );
  };

  const parseFile = async (f) => {
    const wb = new ExcelJS.Workbook();
    await wb.xlsx.load(await f.arrayBuffer());
    const ws = wb.worksheets[0];
    if (!ws) throw new Error("The file has no sheet.");

    // map header text -> column key (ignores case, spaces, "*" marks)
    const lookup = new Map();
    columns.forEach((c) => {
      [c.header, c.key, ...(c.aliases || [])].forEach((n) =>
        lookup.set(norm(n), c.key),
      );
    });
    const colIndex = {};
    ws.getRow(1).eachCell((cell, idx) => {
      const key = lookup.get(norm(cellText(cell.value)));
      if (key && !colIndex[key]) colIndex[key] = idx;
    });
    const missing = columns.filter((c) => c.required && !colIndex[c.key]);
    if (missing.length) {
      throw new Error(
        `Missing column(s): ${missing.map((c) => c.header).join(", ")}. Use the template.`,
      );
    }

    const rows = [];
    ws.eachRow((row, rowNumber) => {
      if (rowNumber === 1) return;
      const item = { row: rowNumber };
      let any = false;
      columns.forEach((c) => {
        const text = colIndex[c.key] ? cellText(row.getCell(colIndex[c.key]).value) : "";
        item[c.key] = text;
        if (text) any = true;
      });
      if (any) rows.push(item);
    });
    return rows;
  };

  const handleUpload = async () => {
    if (!file) return;
    try {
      setBusy(true);
      setResult(null);
      const rows = await parseFile(file);
      if (!rows.length) {
        Swal.fire({ icon: "warning", title: "Nothing to upload", text: "The file has no data rows." });
        return;
      }
      const data = await onSubmit(rows);
      setResult(data);
      if (data.created > 0 && onDone) onDone(data);
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) {
      // all-or-nothing uploads reject the file with a list of bad rows
      const rowErrors = err?.response?.data?.errors;
      if (Array.isArray(rowErrors) && rowErrors.length) {
        setResult({ created: 0, failed: rowErrors.length, errors: rowErrors, message: err.response.data.message });
        return;
      }
      Swal.fire({
        icon: "error",
        title: "Upload failed",
        text:
          err?.response?.data?.message ||
          err?.message ||
          "Could not read or upload the file.",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card">
      <div className="card-header">
        <h5 className="card-title mb-0">
          <i className="fa fa-upload me-2 text-primary" />
          {title}
        </h5>
      </div>
      <div className="card-body">
        {description && <p className="text-muted small mb-3">{description}</p>}
        <button
          type="button"
          className="btn btn-outline-primary btn-sm mb-3"
          onClick={downloadTemplate}
        >
          <i className="fa fa-download me-1" /> Download Excel template
        </button>
        <div className="d-flex gap-2 align-items-center flex-wrap">
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx"
            className="form-control form-control-sm"
            style={{ maxWidth: 320 }}
            onChange={(e) => {
              setFile(e.target.files?.[0] || null);
              setResult(null);
            }}
            disabled={busy}
          />
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={handleUpload}
            disabled={!file || busy}
          >
            {busy ? "Uploading…" : "Upload"}
          </button>
        </div>

        {result && (
          <div className="mt-3">
            <div
              className={`alert ${result.failed ? "alert-warning" : "alert-success"} py-2 mb-2`}
            >
              {result.message || (
                <>
                  {result.created} saved
                  {result.failed ? `, ${result.failed} skipped` : ""}.
                </>
              )}
            </div>
            {result.errors?.length > 0 && (
              <div className="table-responsive" style={{ maxHeight: 260 }}>
                <table className="table table-sm table-bordered mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: 70 }}>Row</th>
                      <th>Item</th>
                      <th>Problem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.errors.map((e, i) => (
                      <tr key={i}>
                        <td>{e.row}</td>
                        <td>{e.skuName || "—"}</td>
                        <td>{e.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkUploadCard;
